import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { supabase, supabaseConfigured } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'

const DEFAULT_PAGE_SIZE = 30

// Cross-source dedup. Multiple BBC slugs (Sport, Football, World)
// happily re-publish the same story under different external_ids,
// so the (source_id, external_id) DB constraint can't catch them.
// We compute a small fingerprint from the first ~60 chars of the
// lowercased title and skip new articles whose fingerprint we've
// already shown. Trades absolute correctness (rare false-positive
// dedup) for a much cleaner feed in the common case.
function articleFingerprint(article) {
  if (!article?.title) return null
  return article.title
    .toLowerCase()
    .replace(/[^a-z0-9؀-ۿ]+/gu, '')
    .slice(0, 60)
}

// Module-level cache for the article list. Keyed on a stable
// signature of the active filters so the hook can hydrate state
// synchronously when the user returns from a /phoenix/news/:id
// detail page or flips between two filter combos they've already
// visited.
//
// Why a module-level Map instead of React Query / SWR? The data
// surface here is tiny (one feed per filter combo, max ~20 combos
// per session), the freshness requirements are loose, and adding a
// dependency for this would dwarf the gains. The Map clears on hard
// reload; pull-to-refresh writes through.
//
// Each entry is a snapshot of *everything* useNewsArticles needs to
// resume rendering: the materialised list, the cursor we'd page
// from, hasMore, and the title fingerprints we've seen (so a
// loadMore after hydration doesn't re-emit duplicates).
const ARTICLES_CACHE = new Map()

function buildArticlesSignature({
  language, category, sourceId, sourceKind, kindGroup, topic, search, mutedSourceIds, view, mediaType, redditSort, pageSize,
}) {
  // Muted-id set must be order-independent so re-mounts that pass
  // the array in a different order still hit the same cache row.
  const muted = (mutedSourceIds || []).slice().sort().join(',')
  return [
    language || '',
    category || '',
    sourceId || '',
    sourceKind || '',
    // kindGroup is the route-level scope ('social' / 'news' / null).
    // Including it in the signature keeps /news and /social on
    // separate cache rows — otherwise whichever loaded first wins
    // and the other shows its articles.
    kindGroup || '',
    topic || '',
    search || '',
    muted,
    view || 'all',
    mediaType || '',
    redditSort || '',
    pageSize,
  ].join('|')
}

// Per-hook caches for the single-row surfaces (digest / trending /
// source list). Same pattern as ARTICLES_CACHE — keyed by the
// argument tuple that fully determines the request.
const DIGEST_CACHE = new Map()        // `${language}|${kind}` → row
const TRENDING_CACHE = new Map()      // `${language}|${category}` → topics[]

// Set-based id caches for saved / read / muted. Single-user app, so
// one slot per cache is enough. NewsPage unmounts when the user opens
// an article (see /article/:id sibling route) and remounts on back
// — hydrating from these caches keeps the list snappy without an
// extra DB round-trip on every back-nav.
let SAVED_IDS_CACHE = null            // Set<articleId> | null
let READ_IDS_CACHE = null             // Set<articleId> | null
let MUTED_IDS_CACHE = null            // Set<sourceId>  | null
let SOURCES_CACHE = null              // sources[] (no filter)

// Curated source list. Cached for the filter chips and the source
// badge on each article card. Read-only via RLS. Module-level cache
// so re-mounting the news page after a detail-page visit doesn't
// flash an empty chip row while the list re-fetches.
export function useNewsSources() {
  const { user } = useAuth()
  const [sources, setSources] = useState(() => SOURCES_CACHE || [])
  const [loading, setLoading] = useState(() => !SOURCES_CACHE)
  const [tableMissing, setTableMissing] = useState(false)

  const fetch = useCallback(async () => {
    if (!supabaseConfigured || !user) {
      setLoading(false)
      return
    }
    const { data, error } = await supabase
      .from('news_sources')
      .select('id, slug, name, category, language, kind, visibility, last_fetched_at, avatar_url, subscriber_count, video_count')
      .eq('enabled', true)
      .order('name', { ascending: true })
    if (error) {
      if (
        error.code === 'PGRST205'
        || /schema cache/i.test(error.message || '')
        || /does not exist/i.test(error.message || '')
      ) {
        setTableMissing(true)
      }
    } else {
      const next = data || []
      SOURCES_CACHE = next
      setSources(next)
      setTableMissing(false)
    }
    setLoading(false)
  }, [user])

  useEffect(() => {
    // Only fetch when we don't already have a cached snapshot. Pull-
    // to-refresh calls refetch directly to bypass this.
    if (!SOURCES_CACHE) fetch()
  }, [fetch])

  return { sources, loading, tableMissing, refetch: fetch }
}

// Single article by id (used by NewsArticlePage). Joined with the
// source so the detail header can show the badge without a second
// round-trip.
export function useNewsArticle(id) {
  const { user } = useAuth()
  const [article, setArticle] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetch = useCallback(async () => {
    if (!supabaseConfigured || !user || !id) {
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    const { data, error: err } = await supabase
      .from('news_articles')
      .select('id, title, link, summary, ai_summary, full_content, content_fetched_at, content_error, translations, image_url, tags, published_at, language, category, media_kind, media_images, media_video_url, media_view_count, media_like_count, comment_count, author, author_flair, post_flair, crosspost_origin, source:news_sources(id, slug, name, homepage_url, kind, avatar_url, subscriber_count)')
      .eq('id', id)
      .maybeSingle()
    if (err) setError(err.message)
    else setArticle(data || null)
    setLoading(false)
  }, [user, id])

  useEffect(() => { fetch() }, [fetch])

  return { article, loading, error, refetch: fetch }
}

// Article list with optional filters + pagination. Returns the next
// page of articles matching the filters, joined with their source
// for the badge.
//
// `view` controls what's shown:
//   - 'all'    — every article, newest first
//   - 'saved'  — only the user's bookmarked articles, save-time order
//   - 'unread' — every article minus the ones the user has opened
//
// Pagination: the hook owns the page list. `loadMore` appends the
// next batch using the last loaded article's (published_at, id) as a
// cursor — `id` is the tiebreaker for the (rare) case where two feeds
// publish at the exact same timestamp. `hasMore` flips false when the
// last fetch returned fewer than `pageSize`.
// Resolves a kindGroup string ('social' | 'news') to a set of
// concrete source kinds. Both /news and /social pages use this so
// the same hook serves both routes — the page just passes its mode.
const KIND_GROUPS = {
  social: ['reddit', 'bluesky', 'mastodon'],
  news: ['news'],
}

export function useNewsArticles({
  language,
  category,
  sourceId,
  sourceKind,
  kindGroup,
  topic,
  search,
  mutedSourceIds,
  view = 'all',
  mediaType,
  redditSort,
  pageSize = DEFAULT_PAGE_SIZE,
} = {}) {
  const { user } = useAuth()

  // Signature of every input that affects what we query — drives the
  // module-level cache lookup. Filter changes recompute it; coming
  // back to a filter combo we've seen this session hits the cache.
  const signature = useMemo(
    () => buildArticlesSignature({
      language, category, sourceId, sourceKind, kindGroup, topic, search, mutedSourceIds, view, mediaType, redditSort, pageSize,
    }),
    [language, category, sourceId, sourceKind, topic, search, mutedSourceIds, view, mediaType, redditSort, pageSize],
  )

  // useState initializers fire once on mount; they're the synchronous
  // hydration path. When the cache has the current signature we land
  // here with the previous list, cursor and hasMore already populated
  // — no flash, no skeleton, scroll restore can kick in immediately.
  const cachedInit = ARTICLES_CACHE.get(signature)
  const [articles, setArticles] = useState(() => cachedInit?.articles || [])
  const [loading, setLoading] = useState(() => !cachedInit)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(() => cachedInit?.hasMore ?? true)
  const [error, setError] = useState(null)
  const [tableMissing, setTableMissing] = useState(false)

  // Latest "unread" mode needs to subtract the set of read ids from
  // the visible page — done client-side over each fetched page so the
  // filter composes cleanly with the publish-time cursor. We re-pull
  // the read set here (cheap: one column, one user) instead of
  // depending on useReadArticleIds so the filter stays self-contained.
  const readIdsRef = useRef(new Set())
  // Compound cursor: { publishedAt, id } in 'all' / 'unread' modes,
  // { savedAt } in 'saved' mode. null = first page / exhausted.
  const cursorRef = useRef(cachedInit?.cursor || null)
  // Title fingerprints we've already shown — feeds the cross-source
  // dedup. Reset on filter change so the first page of a fresh
  // filter doesn't inherit the prior set.
  const seenFingerprintsRef = useRef(new Set(cachedInit?.fingerprints || []))

  // Look up source_ids for a kind filter ("reddit" → all 7 reddit
  // source ids). PostgREST can't filter on joined columns the same
  // way it filters on direct columns, so the cleanest path is a
  // small extra round-trip — feed the ids into a .in() on the
  // articles query.
  const resolveKindIds = useCallback(async (kind) => {
    if (!kind && !kindGroup) return null
    // If sourceKind is set, it wins (more specific). Otherwise, fall
    // back to the route-level kindGroup ('social' / 'news').
    const kinds = kind ? [kind] : (KIND_GROUPS[kindGroup] || null)
    if (!kinds) return null
    const { data } = await supabase
      .from('news_sources')
      .select('id')
      .in('kind', kinds)
    return (data || []).map((s) => s.id)
  }, [kindGroup])

  const fetchSavedPage = useCallback(async (cursor) => {
    // Saved-mode: paginate over news_saved_articles ordered by save
    // time, then fetch the matching articles. Cursor here is the
    // save-time of the last saved row, not the article's publish_at.
    let q = supabase
      .from('news_saved_articles')
      .select('article_id, created_at')
      .order('created_at', { ascending: false })
      .limit(pageSize)
    if (cursor?.savedAt) q = q.lt('created_at', cursor.savedAt)
    const { data: savedRows, error: savedErr } = await q
    if (savedErr) return { error: savedErr }
    if (!savedRows?.length) return { rows: [], nextCursor: null }
    const ids = savedRows.map((r) => r.article_id)
    const kindIds = await resolveKindIds(sourceKind)
    let aq = supabase
      .from('news_articles')
      .select('id, title, link, summary, ai_summary, image_url, tags, published_at, category, language, media_duration_seconds, media_view_count, media_like_count, media_is_live, media_is_short, media_kind, media_images, media_video_url, comment_count, top_comment_body, top_comment_author, top_comment_score, author, author_flair, post_flair, crosspost_origin, source:news_sources(slug, name, kind, avatar_url, subscriber_count)')
      .in('id', ids)
    if (kindIds) aq = aq.in('source_id', kindIds)
    if (mutedSourceIds && mutedSourceIds.length > 0) {
      aq = aq.not('source_id', 'in', `(${mutedSourceIds.join(',')})`)
    }
    if (language) aq = aq.eq('language', language)
    if (category) aq = aq.eq('category', category)
    if (sourceId) aq = aq.eq('source_id', sourceId)
    if (mediaType === 'shorts') aq = aq.eq('media_is_short', true)
    else if (mediaType === 'videos') aq = aq.or('media_is_short.is.null,media_is_short.eq.false')
    if (topic) {
      const safe = topic.replace(/[%,]/g, '')
      aq = aq.or(`title.ilike.%${safe}%,summary.ilike.%${safe}%`)
    }
    if (search) {
      // Web-search-style query (handles quoted phrases, OR, etc.).
      // The `search` column is a generated tsvector with title (A)
      // and summary (B) weighted.
      aq = aq.textSearch('search', search, { type: 'websearch', config: 'simple' })
    }
    const { data, error: err } = await aq
    if (err) return { error: err }
    const index = new Map(ids.map((id, i) => [id, i]))
    const rows = [...(data || [])].sort((a, b) => (index.get(a.id) ?? 0) - (index.get(b.id) ?? 0))
    return {
      rows,
      nextCursor: { savedAt: savedRows[savedRows.length - 1].created_at },
    }
  }, [language, category, sourceId, sourceKind, topic, search, mutedSourceIds, mediaType, pageSize, resolveKindIds])

  // Read-mode pagination — mirrors fetchSavedPage but over the
  // news_read_articles table instead. Cursor is read_at DESC so
  // the most-recently-read items come first. The timestamp column
  // is `read_at` per migration_027 — earlier revisions queried
  // `created_at` here and the resulting error left the Read view
  // permanently empty even when rows existed.
  const fetchReadPage = useCallback(async (cursor) => {
    let q = supabase
      .from('news_read_articles')
      .select('article_id, read_at')
      .order('read_at', { ascending: false })
      .limit(pageSize)
    if (cursor?.readAt) q = q.lt('read_at', cursor.readAt)
    const { data: readRows, error: readErr } = await q
    if (readErr) return { error: readErr }
    if (!readRows?.length) return { rows: [], nextCursor: null }
    const ids = readRows.map((r) => r.article_id)
    const kindIds = await resolveKindIds(sourceKind)
    let aq = supabase
      .from('news_articles')
      .select('id, title, link, summary, ai_summary, image_url, tags, published_at, category, language, media_duration_seconds, media_view_count, media_like_count, media_is_live, media_is_short, media_kind, media_images, media_video_url, comment_count, top_comment_body, top_comment_author, top_comment_score, author, author_flair, post_flair, crosspost_origin, source:news_sources(slug, name, kind, avatar_url, subscriber_count)')
      .in('id', ids)
    if (kindIds) aq = aq.in('source_id', kindIds)
    if (mutedSourceIds && mutedSourceIds.length > 0) {
      aq = aq.not('source_id', 'in', `(${mutedSourceIds.join(',')})`)
    }
    if (language) aq = aq.eq('language', language)
    if (category) aq = aq.eq('category', category)
    if (sourceId) aq = aq.eq('source_id', sourceId)
    if (mediaType === 'shorts') aq = aq.eq('media_is_short', true)
    else if (mediaType === 'videos') aq = aq.or('media_is_short.is.null,media_is_short.eq.false')
    if (topic) {
      const safe = topic.replace(/[%,]/g, '')
      aq = aq.or(`title.ilike.%${safe}%,summary.ilike.%${safe}%`)
    }
    if (search) {
      aq = aq.textSearch('search', search, { type: 'websearch', config: 'simple' })
    }
    const { data, error: err } = await aq
    if (err) return { error: err }
    const index = new Map(ids.map((id, i) => [id, i]))
    const rows = [...(data || [])].sort((a, b) => (index.get(a.id) ?? 0) - (index.get(b.id) ?? 0))
    return {
      rows,
      nextCursor: { readAt: readRows[readRows.length - 1].read_at },
    }
  }, [language, category, sourceId, sourceKind, topic, search, mutedSourceIds, mediaType, pageSize, resolveKindIds])

  const fetchPublishedPage = useCallback(async (cursor) => {
    // All-mode / unread-mode use the same publish-time cursor. Both
    // now subtract read ids client-side — the user explicitly wants
    // the main feed to omit articles they've already opened, treating
    // "All" as the unread inbox. Over-fetch by 2x so the post-filter
    // still hands back a reasonable batch even when the user has
    // read everything recent.
    const fetchSize = pageSize * 2
    const kindIds = await resolveKindIds(sourceKind)
    // 'top' sort (Reddit-only) reorders by upvote count. We swap the
    // order clauses and use a score-based cursor instead of the
    // published-at one. NULLS LAST keeps non-Reddit / un-scored rows
    // from clogging the head of the list when the user has the
    // sourceKind chip on "all" while the redditSort is set.
    const sortByScore = redditSort === 'top' && sourceKind === 'reddit'
    let q = supabase
      .from('news_articles')
      .select('id, title, link, summary, ai_summary, image_url, tags, published_at, category, language, media_duration_seconds, media_view_count, media_like_count, media_is_live, media_is_short, media_kind, media_images, media_video_url, comment_count, top_comment_body, top_comment_author, top_comment_score, author, author_flair, post_flair, crosspost_origin, source:news_sources(slug, name, kind, avatar_url, subscriber_count)')
    if (sortByScore) {
      q = q
        .order('media_view_count', { ascending: false, nullsFirst: false })
        .order('id', { ascending: false })
    } else {
      q = q
        .order('published_at', { ascending: false, nullsFirst: false })
        .order('id', { ascending: false })
    }
    q = q.limit(fetchSize)
    if (sortByScore && cursor?.score != null) {
      // Compound cursor: (media_view_count, id) DESC.
      q = q.or(
        `media_view_count.lt.${cursor.score},and(media_view_count.eq.${cursor.score},id.lt.${cursor.id})`,
      )
    } else if (!sortByScore && cursor?.publishedAt) {
      // Compound cursor: (published_at, id) DESC. The OR form mirrors
      // a row-comparison so we don't drop ties.
      q = q.or(
        `published_at.lt.${cursor.publishedAt},and(published_at.eq.${cursor.publishedAt},id.lt.${cursor.id})`,
      )
    }
    if (kindIds) q = q.in('source_id', kindIds)
    if (mutedSourceIds && mutedSourceIds.length > 0) {
      q = q.not('source_id', 'in', `(${mutedSourceIds.join(',')})`)
    }
    if (language) q = q.eq('language', language)
    if (category) q = q.eq('category', category)
    if (sourceId) q = q.eq('source_id', sourceId)
    if (mediaType === 'shorts') q = q.eq('media_is_short', true)
    else if (mediaType === 'videos') q = q.or('media_is_short.is.null,media_is_short.eq.false')
    if (topic) {
      // Strip the obvious PostgREST .or() injection chars (% and ,)
      // so a maliciously-crafted topic doesn't escape the filter.
      const safe = topic.replace(/[%,]/g, '')
      q = q.or(`title.ilike.%${safe}%,summary.ilike.%${safe}%`)
    }
    if (search) {
      q = q.textSearch('search', search, { type: 'websearch', config: 'simple' })
    }
    const { data, error: err } = await q
    if (err) return { error: err }
    let rows = data || []
    // 'all' (default) and 'unread' both subtract reads from the
    // main feed now. The dedicated read view goes through
    // fetchReadPage instead.
    rows = rows.filter((r) => !readIdsRef.current.has(r.id))
    const last = data?.[data.length - 1]
    return {
      rows,
      // The cursor advances by the LAST row of the *raw* batch, not
      // the post-filter result — otherwise we'd loop forever on a
      // page of all-read articles.
      nextCursor: last
        ? sortByScore
          ? { score: last.media_view_count, id: last.id }
          : { publishedAt: last.published_at, id: last.id }
        : null,
      // hasMore is tied to the raw batch size — a full batch means
      // the DB has more rows, even if all were filtered out.
      rawBatchSize: data?.length || 0,
    }
  }, [language, category, sourceId, sourceKind, topic, search, mutedSourceIds, view, mediaType, redditSort, pageSize, resolveKindIds])

  const loadPage = useCallback(async ({ cursor, append }) => {
    if (!supabaseConfigured || !user) {
      setLoading(false)
      return
    }
    if (append) setLoadingMore(true)
    else setLoading(true)
    setError(null)

    // Refresh read-id snapshot at the start of any "first page" load
    // for the views that need to subtract reads from the result. The
    // main 'all' feed now excludes reads too, so it needs the same
    // refresh.
    if (!append && (view === 'all' || view === 'unread')) {
      const { data: readRows } = await supabase
        .from('news_read_articles')
        .select('article_id')
      readIdsRef.current = new Set((readRows || []).map((r) => r.article_id))
    }

    let result
    if (view === 'saved') result = await fetchSavedPage(cursor)
    else if (view === 'read') result = await fetchReadPage(cursor)
    else result = await fetchPublishedPage(cursor)

    if (result.error) {
      const err = result.error
      if (
        err.code === 'PGRST205'
        || /schema cache/i.test(err.message || '')
        || /does not exist/i.test(err.message || '')
      ) {
        setTableMissing(true)
      } else {
        setError(err.message)
      }
      setLoading(false)
      setLoadingMore(false)
      return
    }

    const { rows, nextCursor, rawBatchSize } = result
    // Saved + read views: batch size = post-fetch row count (the
    // cursor table itself is what we paginate over).
    // Published views: batch size = pre-filter row count so we
    // advance through pages even when every row was filtered out.
    const batchSize = (view === 'saved' || view === 'read')
      ? rows.length
      : (rawBatchSize ?? rows.length)
    // Dedup by title fingerprint across pages. The set resets to
    // empty for a first-page load and accumulates on appends.
    if (!append) seenFingerprintsRef.current = new Set()
    const filtered = []
    for (const row of rows) {
      const fp = articleFingerprint(row)
      if (fp && seenFingerprintsRef.current.has(fp)) continue
      if (fp) seenFingerprintsRef.current.add(fp)
      filtered.push(row)
    }
    const nextHasMore = batchSize >= pageSize && !!nextCursor
    setArticles((prev) => {
      const merged = append ? [...prev, ...filtered] : filtered
      // Write-through to the module cache. A subsequent re-mount with
      // the same filters will hydrate from this snapshot synchronously.
      ARTICLES_CACHE.set(signature, {
        articles: merged,
        cursor: nextCursor,
        hasMore: nextHasMore,
        fingerprints: Array.from(seenFingerprintsRef.current),
        fetchedAt: Date.now(),
      })
      return merged
    })
    setHasMore(nextHasMore)
    cursorRef.current = nextCursor
    setTableMissing(false)
    setLoading(false)
    setLoadingMore(false)
  }, [user, view, fetchSavedPage, fetchPublishedPage, fetchReadPage, pageSize, signature])

  const refetch = useCallback(() => {
    cursorRef.current = null
    return loadPage({ cursor: null, append: false })
  }, [loadPage])

  const loadMore = useCallback(() => {
    if (loadingMore || loading || !hasMore || !cursorRef.current) return
    return loadPage({ cursor: cursorRef.current, append: true })
  }, [loadPage, loadingMore, loading, hasMore])

  // Mount + filter-change effect. Two paths:
  //   - Cache hit: hydrate state from the snapshot (already done by
  //     the useState initializers on first mount, redone here when
  //     the *signature* changes mid-life because useState initialisers
  //     don't re-run). No network call — feels instant.
  //   - Cache miss: clear local state and fetch fresh.
  // Pull-to-refresh bypasses this and goes directly to loadPage; it
  // overwrites the cache entry on completion.
  useEffect(() => {
    const cached = ARTICLES_CACHE.get(signature)
    if (cached) {
      cursorRef.current = cached.cursor
      seenFingerprintsRef.current = new Set(cached.fingerprints || [])
      setArticles(cached.articles)
      setHasMore(cached.hasMore)
      // A cache hit means we had a successful fetch at some point —
      // the tables exist. Clear any stale tableMissing flag a prior
      // transient error left behind. Without this, the migration
      // banner can stick around indefinitely once NewsPage starts
      // staying mounted across navigations (via the Outlet refactor).
      setTableMissing(false)
      setError(null)
      setLoading(false)
      return
    }
    cursorRef.current = null
    seenFingerprintsRef.current = new Set()
    setArticles([])
    setHasMore(true)
    setTableMissing(false)
    setError(null)
    // Flip loading=true so the page shows the skeleton instead of the
    // empty state during the cache-miss fetch. Without this, mode
    // changes (e.g. /news → /social) cleared articles to [] but kept
    // loading=false from the prior page, briefly flashing "No
    // articles" before the new list landed.
    setLoading(true)
    loadPage({ cursor: null, append: false })
  }, [signature, loadPage])

  return {
    articles,
    loading,
    loadingMore,
    hasMore,
    error,
    tableMissing,
    refetch,
    loadMore,
  }
}

// Set of article ids the current user has bookmarked. Used by the
// list page to paint the bookmark indicator on each card without
// per-card round-trips, and by the detail page so the bookmark
// button reflects the current state on first paint.
export function useSavedArticleIds() {
  const { user } = useAuth()
  const [savedIds, setSavedIds] = useState(() => SAVED_IDS_CACHE || new Set())
  const [loading, setLoading] = useState(() => !SAVED_IDS_CACHE)

  const fetch = useCallback(async () => {
    if (!supabaseConfigured || !user) {
      setLoading(false)
      return
    }
    const { data, error } = await supabase
      .from('news_saved_articles')
      .select('article_id')
    if (!error && data) {
      const next = new Set(data.map((r) => r.article_id))
      SAVED_IDS_CACHE = next
      setSavedIds(next)
    }
    setLoading(false)
  }, [user])

  useEffect(() => {
    // Skip the fetch when we already have a cached snapshot. Pull-to-
    // refresh calls refetch directly to bypass this.
    if (!SAVED_IDS_CACHE) fetch()
  }, [fetch])

  // Optimistic local mutation so the bookmark button feels instant.
  // The detail page calls these alongside its DB upsert/delete.
  const markSaved = useCallback((id, isSaved) => {
    setSavedIds((prev) => {
      const next = new Set(prev)
      if (isSaved) next.add(id)
      else next.delete(id)
      SAVED_IDS_CACHE = next
      return next
    })
  }, [])

  return { savedIds, loading, refetch: fetch, markSaved }
}

// Save / unsave for a single article. Returns { saved, toggle,
// pending } so the bookmark button can flip state immediately.
// Errors bubble through `error` — the optimistic flip is rolled
// back on failure.
export function useSavedArticle(articleId) {
  const { user } = useAuth()
  const [saved, setSaved] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    let alive = true
    if (!supabaseConfigured || !user || !articleId) {
      setLoaded(true)
      return () => { alive = false }
    }
    setLoaded(false)
    supabase
      .from('news_saved_articles')
      .select('id')
      .eq('article_id', articleId)
      .maybeSingle()
      .then(({ data }) => {
        if (!alive) return
        setSaved(!!data)
        setLoaded(true)
      })
    return () => { alive = false }
  }, [user, articleId])

  const toggle = useCallback(async () => {
    if (!user || !articleId || pending) return
    const nextSaved = !saved
    setPending(true)
    setError(null)
    setSaved(nextSaved) // optimistic
    try {
      if (nextSaved) {
        const { error: upErr } = await supabase
          .from('news_saved_articles')
          .upsert(
            { user_id: user.id, article_id: articleId },
            { onConflict: 'user_id,article_id', ignoreDuplicates: true },
          )
        if (upErr) throw upErr
      } else {
        const { error: delErr } = await supabase
          .from('news_saved_articles')
          .delete()
          .eq('article_id', articleId)
        if (delErr) throw delErr
      }
    } catch (e) {
      setSaved(!nextSaved) // rollback
      setError(e?.message || 'failed')
    } finally {
      setPending(false)
    }
  }, [user, articleId, saved, pending])

  return { saved, loaded, pending, error, toggle }
}

// Set of article ids the current user has opened. Used by the list
// page to dim already-seen cards and to power the "Unread" view via
// client-side filtering. Mirrors useSavedArticleIds in shape.
export function useReadArticleIds() {
  const { user } = useAuth()
  const [readIds, setReadIds] = useState(() => READ_IDS_CACHE || new Set())
  const [loading, setLoading] = useState(() => !READ_IDS_CACHE)

  const fetch = useCallback(async () => {
    if (!supabaseConfigured || !user) {
      setLoading(false)
      return
    }
    const { data, error } = await supabase
      .from('news_read_articles')
      .select('article_id')
    if (!error && data) {
      const next = new Set(data.map((r) => r.article_id))
      READ_IDS_CACHE = next
      setReadIds(next)
    }
    setLoading(false)
  }, [user])

  useEffect(() => {
    if (!READ_IDS_CACHE) fetch()
  }, [fetch])

  // Optimistic local add. Called from useMarkArticleRead alongside
  // the DB upsert so the list dim happens immediately on tap.
  const markRead = useCallback((id) => {
    setReadIds((prev) => {
      if (prev.has(id)) return prev
      const next = new Set(prev)
      next.add(id)
      READ_IDS_CACHE = next
      return next
    })
  }, [])

  const markUnread = useCallback((id) => {
    setReadIds((prev) => {
      if (!prev.has(id)) return prev
      const next = new Set(prev)
      next.delete(id)
      READ_IDS_CACHE = next
      return next
    })
  }, [])

  // Identity-stable toggle. The naive form would close over
  // `readIds` and recreate every time the Set updates — which would
  // make every card receive a fresh `onToggleRead` prop on every
  // tap and tear down React.memo's bailout. Instead we route the
  // current state through a ref so the callback identity is fixed
  // for the lifetime of the hook; only the user / setter identities
  // are real deps, and those are stable themselves.
  const readIdsRef = useRef(readIds)
  readIdsRef.current = readIds
  const toggleRead = useCallback(async (id) => {
    if (!user || !id) return
    const wasRead = readIdsRef.current.has(id)
    if (wasRead) markUnread(id)
    else markRead(id)
    try {
      if (wasRead) {
        const { error } = await supabase
          .from('news_read_articles')
          .delete()
          .eq('article_id', id)
        if (error) throw error
      } else {
        const { error } = await supabase
          .from('news_read_articles')
          .upsert(
            { user_id: user.id, article_id: id },
            { onConflict: 'user_id,article_id', ignoreDuplicates: true },
          )
        if (error) throw error
      }
      // Broadcast so the list-page hook used by NewsArticlePage
      // re-syncs its dim state on next nav.
      window.dispatchEvent(new CustomEvent(NEWS_READ_EVENT, { detail: { id, removed: wasRead } }))
    } catch (_e) {
      // Rollback the optimistic mutation.
      if (wasRead) markRead(id)
      else markUnread(id)
    }
  }, [user, markRead, markUnread])

  // Cross-hook signal: when the detail page upserts a read row it
  // also dispatches NEWS_READ_EVENT. We mirror that into the local
  // set so the list shows the dim on back-navigation immediately.
  // The `removed` flag (added when toggleRead deletes a row) lets
  // a stale tab pick up the new "unread" state too.
  useEffect(() => {
    const handler = (e) => {
      const id = e?.detail?.id
      if (!id) return
      if (e.detail.removed) markUnread(id)
      else markRead(id)
    }
    window.addEventListener(NEWS_READ_EVENT, handler)
    return () => window.removeEventListener(NEWS_READ_EVENT, handler)
  }, [markRead, markUnread])

  return { readIds, loading, refetch: fetch, markRead, markUnread, toggleRead }
}

// Today's AI-extracted trending topics. One row per (date, language,
// category) in news_trending_topics, populated by the news-trending
// edge function. Returns [] when not yet generated — UI hides the
// chip row.
//
// `category` defaults to 'all' (the general feed). /tech passes 'tech'
// so the chips reflect tech headlines instead of the world-news mix.
export function useTodayTrending(language, category = 'all') {
  const { user } = useAuth()
  const cacheKey = `${language || ''}|${category}`
  const cachedInit = language ? TRENDING_CACHE.get(cacheKey) : null
  const [topics, setTopics] = useState(() => cachedInit || [])
  const [loading, setLoading] = useState(() => !cachedInit)

  const fetch = useCallback(async () => {
    if (!supabaseConfigured || !user || !language) {
      setLoading(false)
      return
    }
    const today = new Date().toISOString().slice(0, 10)
    const { data, error } = await supabase
      .from('news_trending_topics')
      .select('topics, generated_at')
      .eq('topic_date', today)
      .eq('language', language)
      .eq('category', category)
      .maybeSingle()
    const next = !error && data?.topics && Array.isArray(data.topics) ? data.topics : []
    TRENDING_CACHE.set(cacheKey, next)
    setTopics(next)
    setLoading(false)
  }, [user, language, category, cacheKey])

  // Hydrate from cache when language or category changes mid-session
  // (e.g. user flips UI language, or hops between /news and /tech).
  // On miss, fall through to fetch.
  useEffect(() => {
    if (!language) return
    const cached = TRENDING_CACHE.get(cacheKey)
    if (cached) {
      setTopics(cached)
      setLoading(false)
      return
    }
    fetch()
  }, [language, category, cacheKey, fetch])

  return { topics, loading, refetch: fetch }
}

// Today's AI-generated digest in a given language. One row per
// (kind, date, language) in news_digests; populated by the news-digest
// edge function on its cron ticks. Returns null when neither the
// requested language nor the fallback language has a row yet — the
// UI then hides the digest card.
//
// `kind` is 'daily' (default) or 'weekly'. Both can coexist for the
// same date so the UI can render them side by side on Sundays.
//
// Falls back to the other language when the requested locale's row
// is missing. Many /tech digests are English-heavy (most tech sources
// publish in English), so an Arabic user otherwise sees nothing for
// hours while the AR row catches up. The returned object carries
// `source_language` so the UI can offer a one-tap Translate button
// when it differs from the requested locale.
export function useTodayDigest(language, kind = 'daily', category = 'all') {
  const { user } = useAuth()
  const digestKey = `${language || ''}|${kind}|${category}`
  const cachedInit = language ? DIGEST_CACHE.get(digestKey) : undefined
  const [digest, setDigest] = useState(() => cachedInit ?? null)
  const [loading, setLoading] = useState(() => cachedInit === undefined)

  const fetch = useCallback(async () => {
    if (!supabaseConfigured || !user || !language) {
      setLoading(false)
      return
    }
    const today = new Date().toISOString().slice(0, 10)
    // For weekly, look back up to 7 days for the most recent row —
    // the cron fires on a fixed weekday so the digest stays visible
    // the whole week. category narrows to the per-beat digest when
    // set (e.g. 'tech'); 'all' is the cross-cutting general one.
    const buildQuery = (lang) => {
      let q = supabase
        .from('news_digests')
        .select('summary, article_count, generated_at, digest_date, kind, category, language')
        .eq('language', lang)
        .eq('kind', kind)
        .eq('category', category)
        .order('digest_date', { ascending: false })
        .limit(1)
      if (kind === 'daily') q = q.eq('digest_date', today)
      else q = q.gte('digest_date', new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString().slice(0, 10))
      return q
    }
    const { data, error } = await buildQuery(language).maybeSingle()
    let chosen = !error && data ? data : null
    // Fallback to the other language so the card stays visible when
    // the locale's row hasn't been generated yet. The UI checks
    // `source_language !== requested` to decide whether to offer the
    // translate toggle.
    if (!chosen) {
      const other = language === 'ar' ? 'en' : 'ar'
      const { data: fb, error: fbErr } = await buildQuery(other).maybeSingle()
      if (!fbErr && fb) chosen = fb
    }
    const next = chosen ? { ...chosen, source_language: chosen.language } : null
    DIGEST_CACHE.set(digestKey, next)
    setDigest(next)
    setLoading(false)
  }, [user, language, kind, category, digestKey])

  // Hydrate from cache on (language, kind, category) changes; fall
  // through to fetch on miss.
  useEffect(() => {
    if (!language) return
    if (DIGEST_CACHE.has(digestKey)) {
      setDigest(DIGEST_CACHE.get(digestKey))
      setLoading(false)
      return
    }
    fetch()
  }, [digestKey, language, fetch])

  return { digest, loading, refetch: fetch }
}

// Similar-articles fetcher for the article detail page. Wraps the
// `news_similar_articles` RPC (migration 039) which finds articles
// whose title tsquery overlaps the source article's title. Returns
// up to `limit` matches in the same language as the source.
export function useSimilarArticles(articleId, limit = 4) {
  const { user } = useAuth()
  const [articles, setArticles] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let alive = true
    if (!supabaseConfigured || !user || !articleId) {
      setLoading(false)
      return () => { alive = false }
    }
    setLoading(true)
    supabase
      .rpc('news_similar_articles', { article_id: articleId, limit_count: limit })
      .then(({ data, error }) => {
        if (!alive) return
        if (!error && Array.isArray(data)) setArticles(data)
        else setArticles([])
        setLoading(false)
      })
    return () => { alive = false }
  }, [user, articleId, limit])

  return { articles, loading }
}

// Per-user source mute list. The set of source_ids the current user
// has chosen to hide from their feed. Same shape pattern as
// useSavedArticleIds — Set in state + optimistic mark/unmark helper
// + DB upsert/delete in toggle.
export function useMutedSources() {
  const { user } = useAuth()
  const [mutedIds, setMutedIds] = useState(() => MUTED_IDS_CACHE || new Set())
  const [loading, setLoading] = useState(() => !MUTED_IDS_CACHE)

  const fetch = useCallback(async () => {
    if (!supabaseConfigured || !user) {
      setLoading(false)
      return
    }
    const { data, error } = await supabase
      .from('news_muted_sources')
      .select('source_id')
    if (!error && data) {
      const next = new Set(data.map((r) => r.source_id))
      MUTED_IDS_CACHE = next
      setMutedIds(next)
    }
    setLoading(false)
  }, [user])

  useEffect(() => {
    if (!MUTED_IDS_CACHE) fetch()
  }, [fetch])

  const toggle = useCallback(async (sourceId) => {
    if (!user || !sourceId) return
    const isMuted = mutedIds.has(sourceId)
    // Optimistic flip; rollback on error.
    setMutedIds((prev) => {
      const next = new Set(prev)
      if (isMuted) next.delete(sourceId)
      else next.add(sourceId)
      MUTED_IDS_CACHE = next
      return next
    })
    try {
      if (isMuted) {
        const { error } = await supabase
          .from('news_muted_sources')
          .delete()
          .eq('source_id', sourceId)
        if (error) throw error
      } else {
        const { error } = await supabase
          .from('news_muted_sources')
          .upsert(
            { user_id: user.id, source_id: sourceId },
            { onConflict: 'user_id,source_id', ignoreDuplicates: true },
          )
        if (error) throw error
      }
    } catch (_e) {
      // Rollback
      setMutedIds((prev) => {
        const next = new Set(prev)
        if (isMuted) next.add(sourceId)
        else next.delete(sourceId)
        MUTED_IDS_CACHE = next
        return next
      })
    }
  }, [user, mutedIds])

  return { mutedIds, loading, toggle, refetch: fetch }
}

// On-demand YouTube search via the youtube-search edge function.
// One call = 100 quota units (search.list) + 1 unit (videos.list)
// against the 10K/day budget — so 100 searches/day max. The hook
// stays inert until the caller invokes `search(query)`; we don't
// auto-fire on query changes because each call is expensive.
//
// Returned videos use the same shape as cached articles (link, title,
// image_url, channel_name, media_*), so the YouTube page can render
// them with its existing VideoCard. The `external: true` flag lets
// the card tag the result as a live YouTube search hit rather than
// a local archive row.
export function useYouTubeSearch() {
  const [videos, setVideos] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [query, setQuery] = useState('')

  const search = useCallback(async (q, { language } = {}) => {
    const trimmed = (q || '').trim()
    if (!trimmed) {
      setVideos([])
      setQuery('')
      setError(null)
      return
    }
    setLoading(true)
    setError(null)
    setQuery(trimmed)
    try {
      const { data, error: err } = await supabase.functions.invoke('youtube-search', {
        body: { query: trimmed, language: language || undefined, max_results: 15 },
      })
      if (err) throw err
      setVideos(Array.isArray(data?.videos) ? data.videos : [])
    } catch (e) {
      setError(e?.message || 'failed')
      setVideos([])
    } finally {
      setLoading(false)
    }
  }, [])

  const reset = useCallback(() => {
    setVideos([])
    setQuery('')
    setError(null)
  }, [])

  return { videos, loading, error, query, search, reset }
}

// On-demand Reddit search via the reddit-search edge function. Like
// useYouTubeSearch, the hook stays inert until the caller invokes
// search(query) — each call costs one Reddit API request and we
// don't want to fire on every keystroke. Returns posts in the same
// shape as cached articles + an `external: true` flag.
export function useRedditSearch() {
  const [articles, setArticles] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [query, setQuery] = useState('')

  const search = useCallback(async (q, { sort, time, subreddit } = {}) => {
    const trimmed = (q || '').trim()
    if (!trimmed) {
      setArticles([])
      setQuery('')
      setError(null)
      return
    }
    setLoading(true)
    setError(null)
    setQuery(trimmed)
    try {
      const { data, error: err } = await supabase.functions.invoke('reddit-search', {
        body: {
          query: trimmed,
          sort: sort || 'relevance',
          time: time || 'week',
          subreddit: subreddit || undefined,
          limit: 20,
        },
      })
      if (err) throw err
      setArticles(Array.isArray(data?.articles) ? data.articles : [])
    } catch (e) {
      setError(e?.message || 'failed')
      setArticles([])
    } finally {
      setLoading(false)
    }
  }, [])

  const reset = useCallback(() => {
    setArticles([])
    setQuery('')
    setError(null)
  }, [])

  return { articles, loading, error, query, search, reset }
}

// Fetch the top comments tree for a single Reddit post. Pulls the
// permalink off the article link (which we store as
// "https://www.reddit.com/r/<sub>/comments/<id>/<slug>/") and hits
// the fetch-reddit-comments edge function. Returns a flat-but-nested
// comment list capped at depth 3, 20 top-level entries.
//
// Stays inert until the caller invokes `load()` — typically when the
// user opens the comments section on the article detail page.
export function useRedditComments(articleLink, articleId) {
  const [comments, setComments] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [loaded, setLoaded] = useState(false)
  // Cache state surfaced to the UI so the comments overlay can show
  // "fetched N minutes ago" and a refresh affordance when a stale-
  // looking row came from cache.
  const [cached, setCached] = useState(false)
  const [fetchedAt, setFetchedAt] = useState(null)
  // Server-cached translations the function returned alongside the
  // tree. The frontend hook (RedditCommentsList) hydrates its own
  // translation map from this on mount, so a re-open of a post
  // brings back the user's previous translation without paying for
  // Microsoft Translator again.
  const [translations, setTranslations] = useState(null)

  const permalink = useMemo(() => {
    if (!articleLink) return null
    try {
      const u = new URL(articleLink)
      if (!/(^|\.)reddit\.com$/i.test(u.hostname)) return null
      return u.pathname.replace(/\/$/, '')
    } catch {
      return null
    }
  }, [articleLink])

  const load = useCallback(async (sort = 'top', { force = false } = {}) => {
    if (!permalink) {
      setError('not a reddit link')
      return
    }
    setLoading(true)
    setError(null)
    try {
      const { data, error: err } = await supabase.functions.invoke('fetch-reddit-comments', {
        body: { permalink, article_id: articleId || undefined, sort, force },
      })
      if (err) throw err
      // The function returns 200 with `error` in the body when reddit
      // upstream fails (so the actual error detail isn't swallowed by
      // supabase-js's generic non-2xx wrapper). Surface just the
      // status code ("reddit 403") — `data.detail` is the truncated
      // upstream HTML body (Reddit's bot-block page is full of CSS
      // variable declarations) and isn't useful to the user.
      if (data?.error) {
        setError(data.error)
        setComments([])
        return
      }
      setComments(Array.isArray(data?.comments) ? data.comments : [])
      setCached(!!data?.cached)
      setFetchedAt(data?.fetched_at || null)
      setTranslations(data?.translations || null)
    } catch (e) {
      setError(e?.message || 'failed')
      setComments([])
    } finally {
      // Mark loaded regardless of success/failure so the auto-load
      // effect in RedditCommentsList doesn't keep retrying after an
      // error (which produced a "stuck in loading loop" symptom —
      // loading would flip false on error, the effect re-evaluated
      // with loaded still false, and called load() again indefinitely).
      // The user retries via the explicit refresh button.
      setLoaded(true)
      setLoading(false)
    }
  }, [permalink, articleId])

  return {
    comments,
    loading,
    error,
    loaded,
    cached,
    fetchedAt,
    translations,
    load,
    isReddit: !!permalink,
  }
}

// YouTube watch history.
//
// useRecordWatch returns a stable function that cards call when the
// user taps play on a cached YouTube video. Upserts into
// youtube_watch_history so re-watching just bumps watched_at.
// Fire-and-forget — failures swallowed because "I watched this" is
// not load-bearing for any other feature.
export function useRecordWatch() {
  const { user } = useAuth()
  return useCallback((articleId) => {
    if (!user || !articleId) return
    supabase
      .from('youtube_watch_history')
      .upsert(
        { user_id: user.id, article_id: articleId, watched_at: new Date().toISOString() },
        { onConflict: 'user_id,article_id' },
      )
      .then(() => {})
  }, [user])
}

// History list for the YouTube page's History tab. Joins on
// news_articles so the existing VideoCard renderer works. Ordered
// by watched_at DESC; capped at 100 to keep the page snappy.
const WATCH_HISTORY_LIMIT = 100
export function useYouTubeHistory() {
  const { user } = useAuth()
  const [articles, setArticles] = useState([])
  const [loading, setLoading] = useState(true)

  const fetch = useCallback(async () => {
    if (!supabaseConfigured || !user) {
      setLoading(false)
      return
    }
    setLoading(true)
    // Two-step: history rows first, then a single `.in()` over
    // news_articles for the matching ids. PostgREST can't order a
    // joined query by the history's own column cleanly, so this is
    // the simplest path.
    const { data: rows, error } = await supabase
      .from('youtube_watch_history')
      .select('article_id, watched_at')
      .order('watched_at', { ascending: false })
      .limit(WATCH_HISTORY_LIMIT)
    if (error || !rows?.length) {
      setArticles([])
      setLoading(false)
      return
    }
    const ids = rows.map((r) => r.article_id)
    const { data } = await supabase
      .from('news_articles')
      .select('id, title, link, summary, image_url, published_at, language, media_duration_seconds, media_view_count, media_is_live, media_is_short, source:news_sources(slug, name, kind, avatar_url)')
      .in('id', ids)
    const byId = new Map((data || []).map((a) => [a.id, a]))
    // Re-sort to match the watched_at order from the history table
    // (the IN() above doesn't preserve it).
    const ordered = rows
      .map((r, i) => {
        const a = byId.get(r.article_id)
        return a ? { ...a, watched_at: rows[i].watched_at } : null
      })
      .filter(Boolean)
    setArticles(ordered)
    setLoading(false)
  }, [user])

  useEffect(() => { fetch() }, [fetch])

  const clear = useCallback(async () => {
    if (!user) return
    const { error } = await supabase
      .from('youtube_watch_history')
      .delete()
      .eq('user_id', user.id)
    if (!error) setArticles([])
  }, [user])

  return { articles, loading, refetch: fetch, clear }
}

// Cross-hook signal so useReadArticleIds (in NewsPage) can optimistically
// dim a card the moment the user opens it — without waiting for the
// next refetch or pull-to-refresh.
const NEWS_READ_EVENT = 'news:read'

// Side-effect hook used by the article detail page: when the article
// becomes available, upsert a (user, article) row into
// news_read_articles. Best-effort — failures are swallowed because
// "I've seen this" is not load-bearing.
export function useMarkArticleRead(articleId) {
  const { user } = useAuth()
  const firedRef = useRef(false)

  useEffect(() => {
    if (!supabaseConfigured || !user || !articleId) return
    // Guard against StrictMode double-mount + same-article navigation
    // collapsing the second render. Per-article ref reset below.
    if (firedRef.current) return
    firedRef.current = true
    // Optimistically broadcast first so the list dim doesn't wait on
    // the network round-trip. Listeners (useReadArticleIds) add the
    // id to their local set immediately.
    window.dispatchEvent(new CustomEvent(NEWS_READ_EVENT, { detail: { id: articleId } }))
    supabase
      .from('news_read_articles')
      .upsert(
        { user_id: user.id, article_id: articleId },
        { onConflict: 'user_id,article_id', ignoreDuplicates: true },
      )
      // Read-tracking is opportunistic — never surface errors to the
      // user. Worst case the row is missed and the article stays
      // bright in the list.
      .then(() => {})
  }, [user, articleId])

  // Reset the guard when the article id changes so the user navigating
  // article A → article B re-fires the upsert for B.
  useEffect(() => {
    firedRef.current = false
  }, [articleId])
}

